import { IonButton, IonContent, IonPage, useIonToast } from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AutomationActionPickerModal } from '../../components/automation/AutomationActionPickerModal';
import { AutomationBuilderHeader } from '../../components/automation/AutomationBuilderHeader';
import { AutomationDryRunPanel } from '../../components/automation/AutomationDryRunPanel';
import { AutomationRunHistory } from '../../components/automation/AutomationRunHistory';
import {
  AutomationEditorTabs,
  type AutomationEditorTab,
} from '../../components/automation/AutomationEditorTabs';
import {
  WorkflowPlainEnglishSummary,
  WorkflowStepList,
} from '../../components/automation/WorkflowStepList';
import { pickDirectory } from '../../helper/pickDirectory';
import useAutomationWorkflows from '../../hooks/useAutomationWorkflows';
import {
  applyRunProgress,
  emptyRunState,
  startRunState,
} from '../../automation/runProgress';
import { subscribeToRunProgress } from '../../services/Automations.service';
import { formatRelativeSavedTime } from '../../automation/workflowSummary';
import {
  appendLinearAction,
  ensureLinearWorkflow,
  getLinearActionNodes,
  removeLinearAction,
  reorderLinearAction,
  validateWorkflowGraph,
} from '../../automation/workflowGraph';
import {
  createBlankAutomationGraph,
  extractTriggerFromGraph,
  updateNodeConfig,
  updateTriggerOnGraph,
  type AutomationActionType,
  type AutomationGraph,
  type AutomationTrigger,
} from '../../types/AutomationWorkflow.types';
import isElectron from '../../utils/isElectron';
import './Automations.scss';

type RouteParams = { id?: string };

const defaultFileAddedTrigger = (): AutomationTrigger => ({
  type: 'file_added',
  config: {
    extensions: 'mp3, wav, flac, aiff, m4a',
    includeSubfolders: true,
  },
});

const AutomationEditorPage: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  const isNew = !id || id === 'new';
  const numericId = isNew ? null : Number(id);
  const { automations, save, start } = useAutomationWorkflows();
  const [presentToast] = useIonToast();

  const existing = useMemo(
    () =>
      numericId != null
        ? automations.find((automation) => automation.id === numericId)
        : null,
    [automations, numericId],
  );

  const [name, setName] = useState('Untitled automation');
  const [enabled, setEnabled] = useState(false);
  const [trigger, setTrigger] = useState<AutomationTrigger>(
    defaultFileAddedTrigger,
  );
  const [graph, setGraph] = useState<AutomationGraph>(
    createBlankAutomationGraph(),
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<AutomationEditorTab>('builder');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [runState, setRunState] = useState(emptyRunState());

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPickerOpen(true);
      }
      if (event.key === 'Escape') setPickerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(
    () =>
      subscribeToRunProgress((progress) => {
        if (numericId != null && progress.automationId !== numericId) return;
        setRunState((current) => applyRunProgress(current, progress));
        if (progress.status === 'error' && progress.message) {
          presentToast({
            message: progress.message,
            duration: 4000,
            color: 'warning',
          });
        }
      }),
    [numericId, presentToast],
  );

  useEffect(() => {
    if (isNew || !existing) return;
    setName(existing.name);
    setEnabled(existing.enabled);
    setTrigger(existing.trigger ?? { type: 'manual', config: {} });
    setGraph(
      ensureLinearWorkflow(existing.graph ?? createBlankAutomationGraph()),
    );
    setLastSavedAt(new Date(existing.createdAt));
  }, [existing, isNew]);

  const validationIssues = useMemo(
    () => validateWorkflowGraph(graph, trigger),
    [graph, trigger],
  );
  const savedLabel = formatRelativeSavedTime(lastSavedAt);

  const updateTrigger = (next: AutomationTrigger) => {
    setTrigger(next);
    setGraph((current) => updateTriggerOnGraph(current, next));
  };

  const handleAddAction = (actionType: AutomationActionType) => {
    const previousIds = getLinearActionNodes(graph).map((node) => node.id);
    const nextGraph = appendLinearAction(graph, actionType);
    const newNode = getLinearActionNodes(nextGraph).find(
      (node) => !previousIds.includes(node.id),
    );
    setGraph(nextGraph);
    if (newNode) setSelectedNodeId(newNode.id);
    setPickerOpen(false);
  };

  const handlePickTriggerFolder = async () => {
    const folderPath = await pickDirectory({
      title: 'Choose a folder to watch',
      buttonLabel: 'Select folder',
    });
    if (!folderPath) return;
    updateTrigger({
      ...trigger,
      config: { ...trigger.config, folderPath },
    });
  };

  const handleRemoveAction = (nodeId: string) => {
    setGraph((current) => removeLinearAction(current, nodeId));
    if (selectedNodeId === nodeId) setSelectedNodeId('');
  };

  const persist = async () => {
    const saved = await save({
      id: numericId,
      name: name.trim() || 'Untitled automation',
      trigger: extractTriggerFromGraph(graph),
      graph: ensureLinearWorkflow(graph),
      enabled,
    });
    if (saved) setLastSavedAt(new Date());
    return saved;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      presentToast({
        message: 'Name your automation first.',
        duration: 2500,
        color: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await persist();
      if (!saved) return;
      presentToast({
        message: 'Automation saved.',
        duration: 2000,
        color: 'success',
      });
      if (isNew) history.replace(`/automations/${saved.id}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (validationIssues.length) {
      presentToast({
        message: validationIssues[0]?.message ?? 'Fix workflow issues first.',
        duration: 3500,
        color: 'warning',
      });
      return;
    }

    let automationId = numericId;
    if (isNew || !automationId) {
      const saved = await persist();
      if (!saved) return;
      automationId = saved.id;
      history.replace(`/automations/${saved.id}`);
    }

    const runId = await start(automationId);
    if (runId) setRunState(startRunState(runId));
  };

  if (!isElectron()) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <p>Automations are available in the desktop app.</p>
        </IonContent>
      </IonPage>
    );
  }

  if (!isNew && automations.length > 0 && !existing) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <p>That automation no longer exists.</p>
          <IonButton onClick={() => history.push('/automations')}>
            Back
          </IonButton>
        </IonContent>
      </IonPage>
    );
  }

  const executionLog = existing?.executionLog ?? [];

  return (
    <IonPage id="automation-editor-page">
      <IonContent className="automation-editor-content">
        <div className="automation-editor-shell">
          <AutomationBuilderHeader
            name={name}
            savedLabel={savedLabel}
            enabled={enabled}
            saving={saving}
            running={runState.running}
            onNameChange={setName}
            onEnabledChange={setEnabled}
            onRun={() => void handleRun()}
            onSave={() => void handleSave()}
          />

          <AutomationEditorTabs
            active={activeTab}
            automationId={numericId}
            onTabChange={setActiveTab}
          />

          {activeTab === 'builder' && (
            <div className="automation-builder-column">
              <WorkflowPlainEnglishSummary trigger={trigger} graph={graph} />

              <WorkflowStepList
                graph={graph}
                trigger={trigger}
                executionLog={executionLog}
                selectedNodeId={selectedNodeId}
                runState={runState}
                onTriggerChange={updateTrigger}
                onSelectNode={setSelectedNodeId}
                onNodeConfigChange={(nodeId, config) =>
                  setGraph((current) =>
                    updateNodeConfig(current, nodeId, config),
                  )
                }
                onRemove={handleRemoveAction}
                onReorder={(draggedId, targetId) =>
                  setGraph((current) =>
                    reorderLinearAction(current, draggedId, targetId),
                  )
                }
                onPickFolder={handlePickTriggerFolder}
                onAddStep={() => setPickerOpen(true)}
              />

              {getLinearActionNodes(graph).length > 0 && (
                <AutomationDryRunPanel trigger={trigger} graph={graph} />
              )}
            </div>
          )}

          <AutomationActionPickerModal
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={handleAddAction}
          />

          {activeTab === 'run-history' && (
            <AutomationRunHistory executionLog={executionLog} />
          )}

          {activeTab === 'templates' && (
            <section className="automation-templates-panel">
              <h3>Templates</h3>
              <p className="automation-empty-panel">
                Saved recipes are not shipping yet. Build a workflow in the
                Builder tab and it will show up on your Automations list.
              </p>
            </section>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AutomationEditorPage;
